import './OfferDetailFormStyle.less';

import {
  CurrencyInput,
  DatePicker,
  DropdownSelect,
  Icon,
  RadioButtonGroupInput,
} from '@opendoor/bricks';
import { isFinite } from 'lodash';
import moment from 'moment';
import React from 'react';
import { Option } from 'react-select';

import { IListing } from '../../declarations';
import getCurrencyString from '../../helpers/getCurrencyString';

export interface IOfferDetailFormProps {
  closeOfEscrow: any;
  contingentOfferStatus: string;
  contingentOfferStatusOptions: Option[];
  financingType: string;
  financingTypeOptions: Option[];
  hasBeenSubmitted: boolean;
  isContingentOffer: boolean;
  listingInfo: IListing;
  maxAllowedOfferPriceCents: number;
  offerPrice: number;
  onCloseOfEscrowChange: (date: any) => void;
  onContingentOfferStatusChange: (contingentOfferStatus: string) => void;
  onFinancingTypeChange: (financingType: string) => void;
  onIsContingencyOfferChange: (isContingentOffer: boolean) => void;
  onOfferPriceChange: (newValue: number, isValid: boolean) => void;
  onSellerConcessionChange: (newValue: number) => void;
  sellerConcession: number;
}

export const DATE_FORMAT = 'YYYY/M/D';

export default class OfferDetailForm extends React.PureComponent<IOfferDetailFormProps> {
  public handleOfferPriceChange = (value: number) => {
    const { onOfferPriceChange } = this.props;
    onOfferPriceChange(value, this.isValidOfferPrice(value));
  };

  public isValidOfferPrice = (value: number) => {
    const { maxAllowedOfferPriceCents } = this.props;
    return isFinite(value) && value <= maxAllowedOfferPriceCents && value > 0;
  };

  public isAllowedCOEDay = (date: Date) => {
    const validCOEDates = this.props.listingInfo.coe_available_dates;
    const dateMoment = moment(date);
    const isValidDate = validCOEDates.some(coeDate => {
      return moment(coeDate, DATE_FORMAT).isSame(dateMoment, 'day');
    });
    return isValidDate;
  };

  public render(): JSX.Element {
    const {
      closeOfEscrow,
      contingentOfferStatus,
      contingentOfferStatusOptions,
      financingType,
      financingTypeOptions,
      isContingentOffer,
      maxAllowedOfferPriceCents,
      offerPrice,
      onCloseOfEscrowChange,
      onContingentOfferStatusChange,
      onIsContingencyOfferChange,
      onSellerConcessionChange,
      onFinancingTypeChange,
      sellerConcession,
      hasBeenSubmitted,
    } = this.props;

    return (
      <div className="od-offer-detail-form od-base-box box no-padding">
        <div className="top-section less-padding">
          <section className="row header">
            <div className="col-xs-1">
              <Icon className="right-arrow" name="arrow-right-square" />
            </div>
            <div className="col-xs-10">
              <h6>Step 1 of 3: Provide a few details about the offer</h6>
            </div>
          </section>
          <section className="row body-section">
            <div className="col-xs-offset-1 col-md-7 col-xs-10">
              <p className="sub-header">Key information from your purchase agreement</p>
              <div id="offer-price" className="question">
                <CurrencyInput
                  assertNotNull={hasBeenSubmitted}
                  content={offerPrice}
                  errorMessage={
                    !this.isValidOfferPrice(offerPrice) && offerPrice
                      ? `The offer price is more than the max price of ${getCurrencyString(
                          maxAllowedOfferPriceCents,
                        )}.`
                      : undefined
                  }
                  name="offer-price"
                  onChange={(name, value) => this.handleOfferPriceChange(value)}
                  placeholder="$150,000"
                  subtitle="* Required"
                  title="Offer price"
                />
              </div>
              <div id="seller-concession" className="question">
                <CurrencyInput
                  onChange={(name, value) => onSellerConcessionChange(value)}
                  content={sellerConcession}
                  placeholder="$0"
                  title="Seller concession"
                  subtitle="* Required"
                  name="seller-concession"
                  assertNotNull={hasBeenSubmitted}
                />
              </div>
              <div className="question">
                <DatePicker
                  isDayBlocked={date => !this.isAllowedCOEDay(date)}
                  title="Close of escrow"
                  subtitle="* Required"
                  onDateChange={date => onCloseOfEscrowChange(moment(date))}
                  date={closeOfEscrow && closeOfEscrow.toDate()}
                  assertNotNull={hasBeenSubmitted}
                />
              </div>
              <div className="question">
                <DropdownSelect
                  onChange={onFinancingTypeChange}
                  options={financingTypeOptions}
                  placeholder="Select loan type"
                  selectedValue={financingType}
                  title="Financing type"
                  subtitle="* Required"
                  assertNotNull={hasBeenSubmitted}
                />
              </div>
              <RadioButtonGroupInput
                options={[
                  { label: 'Yes', inputName: 'Yes', value: 'true' },
                  { label: 'No', inputName: 'No', value: 'false' },
                ]}
                title="Is this offer contingent on the sale of another home?"
                subtitle="* Required"
                onChange={value => onIsContingencyOfferChange(value === 'true')}
                selectedValue={isContingentOffer === null ? null : String(isContingentOffer)}
                verticallyAligned={false}
                assertNotNull={hasBeenSubmitted}
              />
              {isContingentOffer && (
                <div id="contigency-options" className="question">
                  <DropdownSelect
                    onChange={onContingentOfferStatusChange}
                    options={contingentOfferStatusOptions}
                    selectedValue={contingentOfferStatus}
                    title="What is the status of the contingent home?"
                  />
                </div>
              )}
            </div>
          </section>
        </div>
        <div className="extra-margin" />
      </div>
    );
  }
}
